/**
 * Bloc de gestion de la galerie primaire (lieu ou organisation).
 * Permet d'ajouter, supprimer et réordonner les photos (auto-approuvées).
 */

import { useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { PrimaryGallery, PrimaryGalleryPhoto } from '../types'
import {
  uploadPrimaryGalleryPhoto,
  deletePrimaryGalleryPhoto,
  reorderPrimaryGalleryPhotos,
  uploadOrganizationPrimaryGalleryPhoto,
  deleteOrganizationPrimaryGalleryPhoto,
  reorderOrganizationPrimaryGalleryPhotos,
  HttpError,
} from '../services/organizationApi'
import { normalizePhotoUrl } from '../../../shared/api/runtimeConfig'
import './PrimaryGalleryEditor.css'
import { useTranslation } from 'react-i18next'

function getPlaceholderImage(text: string): string {
  const safeText = text.slice(0, 32)
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" role="img" aria-label="${safeText}">
  <rect width="100%" height="100%" fill="#f3f4f6"/>
  <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="system-ui" font-size="14" fill="#6b7280">${safeText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</text>
</svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

interface PrimaryGalleryEditorProps {
  organizationId: number
  /** Si omis : galerie de l'organisation elle-même */
  locationId?: number
  primaryGallery: PrimaryGallery | null | undefined
  /** 'camping' pour camping, sinon refuge (ignoré si pas de locationId) */
  locationType?: string
  disabled?: boolean
  /** Titre affiché (défaut selon le contexte) */
  title?: string
  hint?: string
}

export function PrimaryGalleryEditor({
  organizationId,
  locationId,
  primaryGallery,
  locationType,
  disabled = false,
  title,
  hint,
}: PrimaryGalleryEditorProps) {
  const { t } = useTranslation('organizations')
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [reordering, setReordering] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isOrgGallery = locationId == null
  const photos = Array.isArray(primaryGallery?.photos) ? primaryGallery!.photos : []
  const sortedPhotos = [...photos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  const invalidate = () => {
    if (isOrgGallery) {
      queryClient.invalidateQueries({ queryKey: ['organization', organizationId] })
    } else {
      queryClient.invalidateQueries({
        queryKey: ['organization', organizationId, 'location', locationId],
      })
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || !file.type.startsWith('image/')) return
    setError(null)
    setUploading(true)
    try {
      if (isOrgGallery) {
        await uploadOrganizationPrimaryGalleryPhoto(organizationId, { image: file })
      } else {
        await uploadPrimaryGalleryPhoto(organizationId, locationId, { image: file }, { locationType })
      }
      invalidate()
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message || t('PrimaryGalleryEditor.erreur_lors_upload'))
      } else {
        setError(t('PrimaryGalleryEditor.erreur_lors_upload'))
      }
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (photo: PrimaryGalleryPhoto) => {
    setError(null)
    setDeletingId(photo.id)
    try {
      if (isOrgGallery) {
        await deleteOrganizationPrimaryGalleryPhoto(organizationId, photo.id)
      } else {
        await deletePrimaryGalleryPhoto(organizationId, locationId, photo.id, { locationType })
      }
      invalidate()
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message || t('PrimaryGalleryEditor.erreur_lors_suppression'))
      } else {
        setError(t('PrimaryGalleryEditor.erreur_lors_suppression'))
      }
    } finally {
      setDeletingId(null)
    }
  }

  const handleMove = async (photo: PrimaryGalleryPhoto, direction: 'up' | 'down') => {
    const idx = sortedPhotos.findIndex((p) => p.id === photo.id)
    if (idx < 0) return
    const newIdx = direction === 'up' ? idx - 1 : idx + 1
    if (newIdx < 0 || newIdx >= sortedPhotos.length) return
    const newOrder = [...sortedPhotos]
    ;[newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]]
    const photoIds = newOrder.map((p) => p.id)
    setError(null)
    setReordering(true)
    try {
      if (isOrgGallery) {
        await reorderOrganizationPrimaryGalleryPhotos(organizationId, photoIds)
      } else {
        await reorderPrimaryGalleryPhotos(organizationId, locationId, photoIds, { locationType })
      }
      invalidate()
    } catch (err) {
      if (err instanceof HttpError) {
        setError(err.message || t('PrimaryGalleryEditor.erreur_lors_reordonnancement'))
      } else {
        setError(t('PrimaryGalleryEditor.erreur_lors_reordonnancement'))
      }
    } finally {
      setReordering(false)
    }
  }

  const sectionTitle = title ?? (isOrgGallery ? t('PrimaryGalleryEditor.galerie_organisation') : t('PrimaryGalleryEditor.galerie_primaire'))
  const sectionHint =
    hint ??
    (isOrgGallery
      ? t('PrimaryGalleryEditor.photos_ajoutees_ici_sont_approuvees')
      : t('PrimaryGalleryEditor.photos_ajoutees_ici_sont_approuvees_2'))

  return (
    <section
      className="primary-gallery-editor"
      aria-labelledby="primary-gallery-editor-title"
    >
      <h3 id="primary-gallery-editor-title" className="primary-gallery-editor__title">
        {sectionTitle}
      </h3>
      <p className="primary-gallery-editor__hint">{sectionHint}</p>
      {error && (
        <div className="primary-gallery-editor__error" role="alert">
          {error}
        </div>
      )}
      <div className="primary-gallery-editor__upload">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleUpload}
          disabled={disabled || uploading}
          className="primary-gallery-editor__file-input"
          aria-label={t('PrimaryGalleryEditor.ajouter_photo')}
        />
        <button
          type="button"
          className="primary-gallery-editor__btn primary-gallery-editor__btn--add"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? t('PrimaryGalleryEditor.ajout') : t('PrimaryGalleryEditor.ajouter_photo_2')}
        </button>
      </div>
      {sortedPhotos.length > 0 ? (
        <div className="primary-gallery-editor__grid">
          {sortedPhotos.map((photo, idx) => (
            <div key={photo.id} className="primary-gallery-editor__card">
              <div className="primary-gallery-editor__thumb">
                <img
                  src={
                    normalizePhotoUrl(photo.image_url ?? (photo as { image?: string }).image) ||
                    getPlaceholderImage(photo.caption || 'Photo')
                  }
                  alt={photo.caption || t('PrimaryGalleryEditor.photo')}
                  className="primary-gallery-editor__img"
                  loading="lazy"
                  onError={(e) => {
                    const target = e.currentTarget
                    if (target.src && !target.dataset.fallback) {
                      target.dataset.fallback = '1'
                      target.src = getPlaceholderImage(photo.caption || 'Photo')
                    }
                  }}
                />
              </div>
              <div className="primary-gallery-editor__actions">
                <button
                  type="button"
                  className="primary-gallery-editor__btn primary-gallery-editor__btn--icon"
                  onClick={() => handleMove(photo, 'up')}
                  disabled={disabled || reordering || idx === 0}
                  title={t('PrimaryGalleryEditor.deplacer_vers_haut')}
                  aria-label={t('PrimaryGalleryEditor.deplacer_vers_haut')}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="primary-gallery-editor__btn primary-gallery-editor__btn--icon"
                  onClick={() => handleMove(photo, 'down')}
                  disabled={disabled || reordering || idx === sortedPhotos.length - 1}
                  title={t('PrimaryGalleryEditor.deplacer_vers_bas')}
                  aria-label={t('PrimaryGalleryEditor.deplacer_vers_bas')}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="primary-gallery-editor__btn primary-gallery-editor__btn--icon primary-gallery-editor__btn--danger"
                  onClick={() => handleDelete(photo)}
                  disabled={disabled || deletingId === photo.id}
                  title={t('PrimaryGalleryEditor.supprimer')}
                  aria-label={t('PrimaryGalleryEditor.supprimer_photo')}
                >
                  {deletingId === photo.id ? '…' : '✕'}
                </button>
              </div>
              {photo.caption && (
                <p className="primary-gallery-editor__caption">{photo.caption}</p>
              )}
            </div>
          ))}
        </div>
      ) : (
        <p className="primary-gallery-editor__empty">
          {t('PrimaryGalleryEditor.aucune_photo_utilisez_bouton_ajouter')}
        </p>
      )}
    </section>
  )
}
